import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import parse from 'html-react-parser';
import {borderRadius, colors, fontSize, fontWeight, lineHeightRatio, transition, zIndex} from '@/styles/tokens';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';

const problemCardSx = {
    p: {xs: 2.5, md: 3},
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 1.5,
    cursor: 'default',
    '&:hover .problem-icon-wrapper': {
        borderColor: colors.primary.main,
        bgcolor: colors.background.tag
    }
};

const iconWrapperSx = {
    width: 72,
    height: 72,
    minWidth: 72,
    position: 'relative' as const,
    zIndex: zIndex.raised,
    bgcolor: colors.background.default,
    border: `1.5px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.circle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 1,
    transition: `border-color ${transition.base}, background-color ${transition.base}`
};

const connectorLineSx = {
    display: {xs: 'none', md: 'block'},
    position: 'absolute' as const,
    top: '80px',
    left: '12.5%',
    right: '12.5%',
    height: '1px',
    bgcolor: colors.neutral.borderLight,
    zIndex: zIndex.base
};

const dividerRowSx = {
    textAlign: 'center',
    mb: {xs: 5, md: 6},
    mt: {xs: 3, md: 4}
};

const statGridContainerSx = {
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'}
};

const statCardSx = (isLast: boolean) => ({
    bgcolor: colors.background.subtle,
    borderRight: {md: isLast ? 'none' : `1px solid ${colors.neutral.borderLight}`},
    borderBottom: {xs: isLast ? 'none' : `1px solid ${colors.neutral.borderLight}`, md: 'none'},
    p: {xs: 2.5, md: 3},
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    transition: `background ${transition.base}`,
    '&:hover': {
        bgcolor: colors.background.default
    }
});

const bannerSx = {
    bgcolor: colors.background.subtle,
    borderRadius: 2,
    px: {xs: 3, md: 5},
    py: {xs: 3, md: 4},
    mt: {xs: 5, md: 7},
    borderLeft: `4px solid ${colors.accent.orange}`
};

const statValueSx = {
    fontSize: {xs: fontSize[32], sm: fontSize[36]},
    lineHeight: lineHeightRatio.tight,
    color: colors.accent.orange
};

const statSubtitleSx = {
    fontSize: {xs: fontSize[16], sm: fontSize[18]},
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    mt: 1
};

const statBodySx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    lineHeight: lineHeightRatio.relaxed
};

const cardHeadingSx = {
    fontSize: {xs: fontSize[16], sm: fontSize[18]},
    fontWeight: fontWeight.bold,
    color: colors.text.primary
};

const cardBodySx = {
    fontSize: fontSize[16],
    color: colors.text.primary
};

const defaultProblemIcons = [
    <SupportAgentOutlinedIcon key="0" sx={{fontSize: 30, color: colors.primary.main}} />,
    <AccessTimeOutlinedIcon key="1" sx={{fontSize: 30, color: colors.primary.main}} />,
    <PhoneDisabledOutlinedIcon key="2" sx={{fontSize: 30, color: colors.primary.main}} />,
    <AutorenewOutlinedIcon key="3" sx={{fontSize: 30, color: colors.primary.main}} />
];

interface ProblemCard {
    icon?: React.ReactNode;
    heading: string;
    body: string;
}

interface StatCard {
    stat: string;
    statColor: string;
    subtitle: string;
    body: string;
}

interface ChatbotHiddenCostProps {
    heading: string;
    subheading?: string;
    problemCards: ProblemCard[];
    impactDividerText: string;
    statCards: StatCard[];
    bannerText: string;
}

const ChatbotHiddenCost = ({heading, subheading, problemCards, impactDividerText, statCards, bannerText}: ChatbotHiddenCostProps) => {
    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading ? parse(heading) : ''}
                    </Typography>
                    {subheading && (
                        <Typography
                            variant="subtitle1"
                            component="p"
                            className="font-regular-text generative-transforming-subheading"
                            sx={{color: colors.text.primary}}
                        >
                            {parse(subheading)}
                        </Typography>
                    )}
                </Box>

                <Box sx={{position: 'relative'}}>
                    <Box sx={connectorLineSx} />
                    <Grid container spacing={3}>
                        {problemCards.map((card, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Box sx={problemCardSx}>
                                    <Box className="problem-icon-wrapper" sx={iconWrapperSx}>
                                        {card.icon ?? defaultProblemIcons[i]}
                                    </Box>
                                    <Typography variant="h3" component="h3" className="font-headers" sx={cardHeadingSx}>
                                        {card.heading}
                                    </Typography>
                                    <Typography variant="body1" className="font-regular-text" sx={cardBodySx}>
                                        {parse(card.body)}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Box sx={dividerRowSx}>
                    <Typography component="p" fontSize={{xs: 18, sm: 20}} className="font-headers" color={colors.text.primary}>
                        {impactDividerText}
                    </Typography>
                </Box>

                <Box sx={statGridContainerSx}>
                    {statCards.map((card, i) => (
                        <Box key={i} sx={statCardSx(i === statCards.length - 1)}>
                            <Typography component="p" className="font-headers" sx={statValueSx}>
                                {card.stat}
                            </Typography>
                            <Typography variant="h3" component="h3" className="font-headers" sx={statSubtitleSx}>
                                {card.subtitle}
                            </Typography>
                            <Typography variant="body1" className="font-regular-text" sx={statBodySx}>
                                {parse(card.body)}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={bannerSx}>
                    <Typography variant="body1" className="font-regular-text" sx={{fontSize: fontSize[16], color: colors.text.primary}}>
                        {parse(bannerText)}
                    </Typography>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(ChatbotHiddenCost);
